import { useLayoutEffect, type RefObject } from 'react'

/**
 * Positions a portaled dropdown menu against its trigger using viewport-fixed
 * coordinates: right-aligned to the trigger's right edge, opening downward by
 * default and flipping to open upward whenever the menu's measured height
 * doesn't fit in the room remaining below.
 *
 * Fixed coordinates, not `position: absolute` relative to a local ancestor,
 * because the menu must be rendered via `createPortal(..., document.body)` —
 * a deck row (`DeckCard`) clips its own content with `overflow: hidden` for
 * its rounded corners, and a menu positioned inside that row is clipped by it
 * the moment the menu is taller than the row, regardless of which direction
 * it opens. Escaping to a portal is what makes "opens fully visible" true;
 * this hook is only the direction/fit half of that — the caller still owns
 * rendering the menu through a portal.
 *
 * Same measure-then-place approach as `NotInStudyMark`'s popover (the first
 * surface this shape of bug was fixed on): imperative, one-shot DOM writes in
 * a layout effect rather than state, so re-placing never costs an extra
 * render.
 */
export function useFlipMenuPlacement(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  menuRef: RefObject<HTMLElement | null>,
  opts?: { gap?: number; edgePad?: number },
  // Extra values that should force re-measurement without the caller having
  // to know this hook's own dependency list — e.g. a submenu inside the menu
  // that expands its height after the initial open. Omit when the menu's
  // size never changes after mount.
  deps: unknown[] = [],
): void {
  const gap = opts?.gap ?? 6
  const edgePad = opts?.edgePad ?? 8

  // eslint-disable-next-line react-hooks/exhaustive-deps -- `deps` is the caller's explicit extra-recompute signal
  useLayoutEffect(() => {
    if (!open) return
    const anchorEl = anchorRef.current
    const menuEl = menuRef.current
    if (!anchorEl || !menuEl) return

    const anchorRect = anchorEl.getBoundingClientRect()
    const menuRect = menuEl.getBoundingClientRect()

    menuEl.style.position = 'fixed'
    // Right-aligned to the anchor's own right edge, expressed as a distance
    // from the viewport's right edge — this stays correct at the menu's
    // actual (possibly not-yet-known) width, unlike anchoring from `left`.
    menuEl.style.right = `${window.innerWidth - anchorRect.right}px`
    menuEl.style.left = 'auto'

    const spaceBelow = window.innerHeight - anchorRect.bottom - edgePad
    if (spaceBelow < menuRect.height) {
      menuEl.style.top = 'auto'
      menuEl.style.bottom = `${window.innerHeight - anchorRect.top + gap}px`
    } else {
      menuEl.style.top = `${anchorRect.bottom + gap}px`
      menuEl.style.bottom = 'auto'
    }
  }, [open, anchorRef, menuRef, gap, edgePad, ...deps])
}
